/**
 * EnhancedBettingPanel.js - Enhanced Betting Interface for M1 Pug Racing
 * 
 * Handles the 30-second betting phase for "Find the M Token" gameplay with
 * improved UI, quick betting options, and betting slip functionality.
 */

export class EnhancedBettingPanel {
    constructor(uiManager) {
        this.uiManager = uiManager;
        this.isVisible = true;
        this.isBettingActive = false;
        this.countdownTimer = null;
        this.timeRemaining = 30;
        
        // Racer data with recent performance
        this.racers = [
            { 
                id: 1, 
                name: 'Red', 
                color: '#FF0000', 
                icon: '🟥', 
                odds: 2.5, 
                winRate: 45,
                recentFinds: ['🏆', '🥈', '🥉', '❌', '🏆']
            },
            { 
                id: 2, 
                name: 'Green', 
                color: '#00FF00', 
                icon: '🟩', 
                odds: 1.8, 
                winRate: 62,
                recentFinds: ['🏆', '🏆', '🏆', '🥈', '🏆'],
                isFavorite: true
            },
            { 
                id: 3, 
                name: 'Blue', 
                color: '#0000FF', 
                icon: '🟦', 
                odds: 3.2, 
                winRate: 38,
                recentFinds: ['🥈', '🥉', '❌', '❌', '🏆']
            },
            { 
                id: 4, 
                name: 'Yellow', 
                color: '#FFFF00', 
                icon: '🟨', 
                odds: 5.1, 
                winRate: 22,
                recentFinds: ['❌', '❌', '🥉', '❌', '❌']
            },
            { 
                id: 5, 
                name: 'Purple', 
                color: '#FF00FF', 
                icon: '🟪', 
                odds: 4.0, 
                winRate: 31,
                recentFinds: ['🥈', '❌', '🏆', '❌', '🥉']
            },
            { 
                id: 6, 
                name: 'Cyan', 
                color: '#00FFFF', 
                icon: '🟧', 
                odds: 6.5, 
                winRate: 18,
                recentFinds: ['❌', '❌', '❌', '❌', '❌'],
                isUnderdog: true
            }
        ];
        
        // Betting state
        this.currentBets = new Map();
        this.userBalance = 12450;
        this.totalPool = 5000;
        
        console.log('[EnhancedBettingPanel] Initialized');
    }

    /**
     * Initialize the betting panel
     */
    async initialize() {
        this.createPanelStructure();
        this.setupEventListeners();
        this.updateDisplay();
        console.log('[EnhancedBettingPanel] Panel ready');
    }

    /**
     * Create the enhanced panel HTML structure
     */
    createPanelStructure() {
        const container = document.getElementById('enhanced-betting-container');
        if (!container) {
            const newContainer = document.createElement('div');
            newContainer.id = 'enhanced-betting-container';
            newContainer.className = 'enhanced-betting-container';
            document.body.appendChild(newContainer);
        }

        const panelHTML = `
            <div class="enhanced-betting-panel ${this.isVisible ? 'visible' : 'hidden'}">
                <!-- Header Section -->
                <div class="betting-header">
                    <div class="header-title">
                        <span class="icon-money">💰</span>
                        <span class="title-main">BETTING STATION</span>
                        <span class="icon-money">💰</span>
                    </div>
                    <div class="header-countdown">
                        <span class="phase-label">BETTING PHASE ACTIVE</span>
                        <div class="countdown-display">
                            <span class="countdown-icon">⏱️</span>
                            <span class="countdown-value" id="betting-timer">00:30</span>
                            <span class="countdown-label">REMAINING</span>
                        </div>
                    </div>
                </div>

                <!-- Question Section -->
                <div class="betting-question">
                    WHO WILL FIND THE M TOKEN FIRST?
                </div>

                <!-- Racers Table -->
                <div class="racers-table">
                    <table class="racers-grid">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>RACER</th>
                                <th>ODDS</th>
                                <th>WIN%</th>
                                <th>RECENT FINDS</th>
                                <th>YOUR BET</th>
                                <th>QUICK BET</th>
                            </tr>
                        </thead>
                        <tbody id="racers-tbody">
                            <!-- Racer rows will be dynamically generated -->
                        </tbody>
                    </table>
                </div>

                <!-- Betting Slip Section -->
                <div class="betting-slip">
                    <div class="slip-header">YOUR BETTING SLIP</div>
                    <div class="slip-content" id="betting-slip-content">
                        <div class="slip-empty">No bets placed yet</div>
                    </div>
                    <div class="slip-summary">
                        <div class="summary-row">
                            <span class="summary-label">Wallet Balance:</span>
                            <span class="summary-value" id="wallet-balance">$12,450</span>
                        </div>
                        <div class="summary-row" id="after-bet-row" style="display: none;">
                            <span class="summary-label">After Bet:</span>
                            <span class="summary-value" id="after-bet-balance">$12,450</span>
                        </div>
                    </div>
                    <div class="slip-actions">
                        <button class="bet-action-btn place-bet" id="btn-place-bet" disabled>
                            PLACE BET
                        </button>
                        <button class="bet-action-btn clear-all" id="btn-clear-all" disabled>
                            CLEAR ALL
                        </button>
                        <button class="bet-action-btn auto-bet" id="btn-auto-bet">
                            AUTO-BET
                        </button>
                    </div>
                </div>
            </div>
        `;

        document.getElementById('enhanced-betting-container').innerHTML = panelHTML;
        
        // Generate racer rows
        this.generateRacerRows();
    }

    /**
     * Generate racer rows in the table
     */
    generateRacerRows() {
        const tbody = document.getElementById('racers-tbody');
        if (!tbody) return;

        tbody.innerHTML = '';
        
        this.racers.forEach((racer, index) => {
            const row = document.createElement('tr');
            row.className = 'racer-row';
            if (racer.isFavorite) row.classList.add('favorite');
            if (racer.isUnderdog) row.classList.add('underdog');
            
            row.innerHTML = `
                <td class="racer-number">${index + 1}</td>
                <td class="racer-name">
                    <span class="racer-icon">${racer.icon}</span>
                    <span class="racer-text">${racer.name}</span>
                    ${racer.isFavorite ? '<span class="tag favorite-tag">⭐ FAVORITE</span>' : ''}
                    ${racer.isUnderdog ? '<span class="tag underdog-tag">💀 UNDERDOG</span>' : ''}
                </td>
                <td class="racer-odds">
                    <span class="odds-box">[${racer.odds}x]</span>
                </td>
                <td class="racer-winrate">${racer.winRate}%</td>
                <td class="racer-recent">
                    ${racer.recentFinds.join('')}
                </td>
                <td class="racer-bet" id="bet-amount-${racer.id}">
                    <span class="no-bet">---</span>
                </td>
                <td class="racer-actions">
                    <div class="quick-bet-buttons" data-racer-id="${racer.id}">
                        <button class="quick-bet-btn" data-amount="100">[100]</button>
                        <button class="quick-bet-btn" data-amount="500">[500]</button>
                        <button class="quick-bet-btn max" data-amount="max">[MAX]</button>
                    </div>
                </td>
            `;
            
            tbody.appendChild(row);
        });
    }

    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Quick bet buttons
        document.querySelectorAll('.quick-bet-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const racerId = parseInt(e.target.closest('.quick-bet-buttons').dataset.racerId);
                const amount = e.target.dataset.amount;
                this.handleQuickBet(racerId, amount);
            });
        });
        
        // Action buttons
        const placeBetBtn = document.getElementById('btn-place-bet');
        const clearAllBtn = document.getElementById('btn-clear-all');
        const autoBetBtn = document.getElementById('btn-auto-bet');
        
        if (placeBetBtn) {
            placeBetBtn.addEventListener('click', () => this.placeBets());
        }
        
        if (clearAllBtn) {
            clearAllBtn.addEventListener('click', () => this.clearAllBets());
        }
        
        if (autoBetBtn) {
            autoBetBtn.addEventListener('click', () => this.autoBet());
        }
    }

    /**
     * Handle quick bet button click
     */
    handleQuickBet(racerId, amount) {
        if (!this.isBettingActive) {
            this.showNotification('Betting phase has ended!', 'error');
            return;
        }
        
        const racer = this.racers.find(r => r.id === racerId);
        if (!racer) return;
        
        let betAmount;
        if (amount === 'max') {
            betAmount = Math.min(this.userBalance, 5000);
        } else {
            betAmount = parseInt(amount);
        }
        
        if (betAmount > this.userBalance) {
            this.showNotification('Insufficient balance!', 'error');
            return;
        }
        
        // Check if bet already exists
        const existingBet = this.currentBets.get(racerId);
        if (existingBet) {
            // Update existing bet
            const diff = betAmount - existingBet.amount;
            if (this.userBalance < diff) {
                this.showNotification('Insufficient balance for increase!', 'error');
                return;
            }
            existingBet.amount = betAmount;
        } else {
            // Add new bet
            this.currentBets.set(racerId, {
                racer: racer,
                amount: betAmount,
                odds: racer.odds
            });
        }
        
        this.updateDisplay();
        this.highlightBetRow(racerId);
    }

    /**
     * Highlight a bet row
     */
    highlightBetRow(racerId) {
        // Find the row
        const rows = document.querySelectorAll('.racer-row');
        rows.forEach((row, index) => {
            if (this.racers[index].id === racerId) {
                row.classList.add('has-bet');
                
                // Update bet amount display
                const betCell = document.getElementById(`bet-amount-${racerId}`);
                const bet = this.currentBets.get(racerId);
                if (betCell && bet) {
                    betCell.innerHTML = `
                        <span class="bet-amount">$${bet.amount}</span>
                        <button class="modify-bet" data-racer-id="${racerId}">[+/-]</button>
                        <button class="cancel-bet" data-racer-id="${racerId}">[X]</button>
                    `;
                    
                    // Add modify/cancel handlers
                    betCell.querySelector('.modify-bet').addEventListener('click', () => {
                        this.modifyBet(racerId);
                    });
                    
                    betCell.querySelector('.cancel-bet').addEventListener('click', () => {
                        this.cancelBet(racerId);
                    });
                }
            }
        });
    }

    /**
     * Cancel a bet
     */
    cancelBet(racerId) {
        this.currentBets.delete(racerId);
        this.updateDisplay();
        
        // Reset row display
        const betCell = document.getElementById(`bet-amount-${racerId}`);
        if (betCell) {
            betCell.innerHTML = '<span class="no-bet">---</span>';
        }
        
        // Remove highlight
        const rows = document.querySelectorAll('.racer-row');
        rows.forEach((row, index) => {
            if (this.racers[index].id === racerId) {
                row.classList.remove('has-bet');
            }
        });
    }

    /**
     * Update display
     */
    updateDisplay() {
        // Update betting slip
        const slipContent = document.getElementById('betting-slip-content');
        if (slipContent) {
            if (this.currentBets.size === 0) {
                slipContent.innerHTML = '<div class="slip-empty">No bets placed yet</div>';
                document.getElementById('after-bet-row').style.display = 'none';
                document.getElementById('btn-place-bet').disabled = true;
                document.getElementById('btn-clear-all').disabled = true;
            } else {
                let slipHTML = '';
                let totalBet = 0;
                let totalPotentialWin = 0;
                
                this.currentBets.forEach((bet, racerId) => {
                    const potentialWin = bet.amount * bet.odds;
                    totalBet += bet.amount;
                    totalPotentialWin += potentialWin;
                    
                    slipHTML += `
                        <div class="slip-item">
                            <span class="slip-racer">${bet.racer.icon} ${bet.racer.name}</span>
                            <span class="slip-bet">$${bet.amount}</span>
                            <span class="slip-odds">${bet.odds}x</span>
                            <span class="slip-win">→ $${potentialWin.toFixed(0)}</span>
                        </div>
                    `;
                });
                
                slipHTML += `
                    <div class="slip-total">
                        <span class="total-label">Total Bet:</span>
                        <span class="total-amount">$${totalBet}</span>
                    </div>
                    <div class="slip-total">
                        <span class="total-label">Potential Win:</span>
                        <span class="total-win">$${totalPotentialWin.toFixed(0)}</span>
                    </div>
                `;
                
                slipContent.innerHTML = slipHTML;
                
                // Update balance display
                document.getElementById('after-bet-row').style.display = 'block';
                document.getElementById('after-bet-balance').textContent = `$${this.userBalance - totalBet}`;
                
                // Enable buttons
                document.getElementById('btn-place-bet').disabled = false;
                document.getElementById('btn-clear-all').disabled = false;
            }
        }
    }

    /**
     * Start betting phase
     */
    startBettingPhase() {
        this.isBettingActive = true;
        this.timeRemaining = 30;
        this.startCountdown();
        this.show();
        console.log('[EnhancedBettingPanel] Betting phase started');
    }

    /**
     * Start countdown
     */
    startCountdown() {
        if (this.countdownTimer) {
            clearInterval(this.countdownTimer);
        }
        
        this.countdownTimer = setInterval(() => {
            this.timeRemaining--;
            this.updateCountdown();
            
            if (this.timeRemaining <= 0) {
                this.endBettingPhase();
            }
        }, 1000);
    }

    /**
     * Update countdown display
     */
    updateCountdown() {
        const timerElement = document.getElementById('betting-timer');
        if (timerElement) {
            const seconds = this.timeRemaining.toString().padStart(2, '0');
            timerElement.textContent = `00:${seconds}`;
            
            // Add urgency styling
            if (this.timeRemaining <= 10) {
                timerElement.classList.add('urgent');
            } else if (this.timeRemaining <= 20) {
                timerElement.classList.add('warning');
            }
        }
    }

    /**
     * End betting phase
     */
    endBettingPhase() {
        this.isBettingActive = false;
        clearInterval(this.countdownTimer);
        
        // Disable all betting controls
        document.querySelectorAll('.quick-bet-btn').forEach(btn => {
            btn.disabled = true;
        });
        
        console.log('[EnhancedBettingPanel] Betting phase ended');
        
        if (this.uiManager) {
            this.uiManager.emit('betting-phase-ended', {
                bets: Array.from(this.currentBets.entries())
            });
        }
    }

    /**
     * Place all bets
     */
    placeBets() {
        if (!this.isBettingActive) {
            this.showNotification('Betting phase has ended!', 'error');
            return;
        }
        
        let totalBet = 0;
        this.currentBets.forEach(bet => {
            totalBet += bet.amount;
        });
        
        if (totalBet > this.userBalance) {
            this.showNotification('Insufficient balance!', 'error');
            return;
        }
        
        // Deduct from balance
        this.userBalance -= totalBet;
        document.getElementById('wallet-balance').textContent = `$${this.userBalance}`;
        
        // Lock in bets
        this.showNotification('Bets placed successfully!', 'success');
        
        // Emit event
        if (this.uiManager) {
            this.uiManager.emit('bets-placed', {
                bets: Array.from(this.currentBets.entries()),
                totalAmount: totalBet
            });
        }
        
        // Disable further betting on these racers
        document.getElementById('btn-place-bet').disabled = true;
    }

    /**
     * Clear all bets
     */
    clearAllBets() {
        this.currentBets.clear();
        
        // Reset all bet displays
        this.racers.forEach(racer => {
            const betCell = document.getElementById(`bet-amount-${racer.id}`);
            if (betCell) {
                betCell.innerHTML = '<span class="no-bet">---</span>';
            }
        });
        
        // Remove all highlights
        document.querySelectorAll('.racer-row').forEach(row => {
            row.classList.remove('has-bet');
        });
        
        this.updateDisplay();
        this.showNotification('All bets cleared', 'info');
    }

    /**
     * Auto bet feature
     */
    autoBet() {
        if (!this.isBettingActive) {
            this.showNotification('Betting phase has ended!', 'error');
            return;
        }
        
        // Find the favorite
        const favorite = this.racers.find(r => r.isFavorite);
        if (favorite) {
            // Bet 10% of balance on favorite
            const betAmount = Math.floor(this.userBalance * 0.1);
            this.handleQuickBet(favorite.id, betAmount.toString());
            this.showNotification(`Auto-bet placed on ${favorite.name}!`, 'success');
        }
    }

    /**
     * Show notification
     */
    showNotification(message, type = 'info') {
        console.log(`[EnhancedBettingPanel] ${type}: ${message}`);
        
        if (this.uiManager) {
            this.uiManager.showNotification(message, type);
        }
    }

    /**
     * Show/hide panel
     */
    show() {
        const panel = document.querySelector('.enhanced-betting-panel');
        if (panel) {
            panel.classList.add('visible');
            panel.classList.remove('hidden');
            this.isVisible = true;
        }
    }

    hide() {
        const panel = document.querySelector('.enhanced-betting-panel');
        if (panel) {
            panel.classList.remove('visible');
            panel.classList.add('hidden');
            this.isVisible = false;
        }
    }

    toggle() {
        if (this.isVisible) {
            this.hide();
        } else {
            this.show();
        }
    }

    /**
     * Cleanup
     */
    destroy() {
        if (this.countdownTimer) {
            clearInterval(this.countdownTimer);
        }
        
        const container = document.getElementById('enhanced-betting-container');
        if (container) {
            container.remove();
        }
    }
}

// Export as ES module
export default EnhancedBettingPanel;